import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Callout } from "@bednbody/ui/components/callout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { ArrowRightIcon, BuildingsIcon, WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { type CashHandoffRow, useCashHandoffs, useConfirmCashHandoffCollected } from "./cash.hooks";
import { CASH_HANDLING_LABELS, handoffPartyLabel, handoffStageBadge } from "./cash.handoffs";
import { isOpenHandoff } from "./cash.queue";

/**
 * The Cash Handling handoffs — the surface CASH § Visibility asks for, and the
 * only place a Hub can close the third one.
 *
 * ⚠ ITS OWN FILE. `CashRemittancePage.tsx` is 492 lines and the standards split
 * a feature file past ~500; this section is also a different table with a
 * different actor, so folding it in would have made one screen answer two
 * questions.
 *
 * ⚠ WHAT THIS DELIBERATELY DOES NOT OFFER. There is no "mark paid" for the
 * payer's half and no Report an Issue here. Both belong to the person physically
 * holding the money — CASH gives the dispute to the recipient at the kerbside,
 * and an office button that could clear a rider's liability from across the city
 * is the hole 0166 and 0180 both closed on purpose.
 */
export function CashHandoffsSection({ days }: { days: number }) {
  const { claims } = useAuth();
  const isHubManager = claims?.role === "hub_manager";
  // 0241: a Super Admin may sign for the Hub when its manager is not on shift.
  // Company-wide, so no hub filter — the same scope Admin already reads with.
  const isSuperadmin = claims?.role === "superadmin";
  const hubId = isHubManager ? (claims?.hub_id ?? null) : null;

  const { data, isLoading, isError } = useCashHandoffs(hubId, days);
  const confirmCollected = useConfirmCashHandoffCollected();
  const [pending, setPending] = useState<CashHandoffRow | null>(null);

  const rows = data ?? [];
  // A reported issue is closed — the cash continues on a NEW handoff.
  const open = rows.filter((r) => isOpenHandoff(r.status));

  /**
   * The hub manager of THIS hub, or a Super Admin for any hub (0241), and only
   * once the payer has confirmed payment — the server's own guards, mirrored so
   * the control is absent rather than certain to be refused.
   */
  const canConfirm = (row: CashHandoffRow) =>
    (isSuperadmin || (isHubManager && row.hub_id === claims?.hub_id)) &&
    row.recipient_kind === "hub" &&
    row.status === "pending_confirmation";

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-foreground">
          Cash Handling{" "}
          <span className="font-normal text-muted-foreground">
            — therapist to rider, rider onward
          </span>
        </h2>
        {open.length > 0 ? <Badge variant="warning">{open.length} in progress</Badge> : null}
      </div>

      {/* The confirm dialog closes on click, so a refusal (wrong hub, payer has
          not paid, already collected) must be said here or it is said nowhere. */}
      {confirmCollected.isError ? (
        <Callout tone="danger" role="alert">
          Couldn&apos;t confirm that handoff: {confirmCollected.error.message}
        </Callout>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-24 motion-reduce:animate-none" />
      ) : isError ? (
        <p className="rounded-card border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Couldn&apos;t load Cash Handling right now.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-card border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No Cash Handling handoffs in this period.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => {
            const stage = handoffStageBadge(row.status);
            return (
              <li key={row.id}>
                <SurfaceCard className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                        <span className="truncate font-medium">
                          {handoffPartyLabel(row.payer_kind, row.payer?.full_name ?? null, null)}
                        </span>
                        <ArrowRightIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium">
                          {handoffPartyLabel(
                            row.recipient_kind,
                            row.recipient?.full_name ?? null,
                            row.hubs?.name ?? null,
                          )}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Leg {row.seq}
                        {row.cash_remittances?.bookings?.ref
                          ? ` · ${row.cash_remittances.bookings.ref}`
                          : ""}
                        {row.hubs?.name ? ` · ${row.hubs.name}` : ""}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm font-medium tabular-nums text-foreground">
                        ₱{Number(row.amount).toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                      </span>
                      <Badge variant={stage.variant}>{stage.label}</Badge>
                      {canConfirm(row) ? (
                        <Button
                          size="sm"
                          onClick={() => {
                            confirmCollected.reset();
                            setPending(row);
                          }}
                          disabled={confirmCollected.isPending}
                        >
                          {CASH_HANDLING_LABELS.recipientConfirm}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {row.issue_reported_at ? (
                    <p className="mt-3 flex items-center gap-2 text-xs text-destructive">
                      <WarningIcon className="size-4 shrink-0" />
                      An issue was reported on this handoff. The cash stayed with the payer and the
                      process continues on a new handoff.
                    </p>
                  ) : null}
                </SurfaceCard>
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={pending !== null} onOpenChange={(next) => !next && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{CASH_HANDLING_LABELS.recipientConfirm}?</AlertDialogTitle>
            {/* CASH: "The Rider counts the Cash and selects Confirm Cash Collected
                only when the exact amount has been received", and the recipient
                "must never keep more than the displayed Cash amount". */}
            <AlertDialogDescription>
              {pending
                ? `Count ₱${Number(pending.amount).toLocaleString("en-PH", {
                    minimumFractionDigits: 2,
                  })} from ${handoffPartyLabel(
                    pending.payer_kind,
                    pending.payer?.full_name ?? null,
                    null,
                  )} before confirming. Take exactly this amount — hand back anything over it, and
                    report an issue instead if it is short. This moves the cash off their balance
                    and is audit-logged.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) confirmCollected.mutate(pending.id);
                setPending(null);
              }}
            >
              <BuildingsIcon />
              {CASH_HANDLING_LABELS.recipientConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
